import React, { useState, useMemo, useEffect } from 'react';
import './ProductTrackingTables.css';
import { DeliveryLog } from '../../../types/deliveryLog';
import { Recipe } from '../../../types';
import { BatchRecord } from '../../../types/batchRecord';
import { createBlankDeliveryLog } from '../../../store/slices/deliveryLogsSlice';
import { groupItemsBySection, getDefaultExpandedState } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { matchesSearchQuery } from '../../../utils/searchUtils';
import { scrollToFieldAndHighlight } from '../../../utils/scrollUtils';

interface DeliveryLogListProps {
  logs: DeliveryLog[];
  fillings: Recipe[];
  batchRecords: BatchRecord[];
  onAdd: (log: DeliveryLog) => void;
  onUpdate: (log: DeliveryLog) => void;
  onDelete: (logId: string) => void;
  targetDeliveryId?: string;
  targetField?: string;
  isFlagged?: boolean;
}

export const DeliveryLogList: React.FC<DeliveryLogListProps> = ({
  logs,
  fillings,
  batchRecords,
  onAdd,
  onUpdate,
  onDelete,
  targetDeliveryId,
  targetField,
  isFlagged
}) => {
  const { openTraceability } = useTraceability();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newLog, setNewLog] = useState<DeliveryLog>(createBlankDeliveryLog());
  const [editLog, setEditLog] = useState<DeliveryLog | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter logs based on search
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logs;
    return logs.filter(log => matchesSearchQuery(log, searchTerm, [log.fillingName || '']));
  }, [logs, searchTerm]);

  // Group logs into sections by deliveryDate
  const sections = useMemo(
    () => groupItemsBySection(filteredLogs, (log) => log.deliveryDate),
    [filteredLogs]
  );

  // Track expanded/collapsed state for each section
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    getDefaultExpandedState(sections)
  );

  // Auto-expand section containing target log for deep-linking
  useEffect(() => {
    if (!targetDeliveryId) return;
    for (const section of sections) {
      if (section.items.some(log => log.id === targetDeliveryId)) {
        setExpandedSections(prev => ({ ...prev, [section.key]: true }));
        break;
      }
    }
  }, [targetDeliveryId, sections]);

  // Field-level deep-linking from admin review
  useEffect(() => {
    if (!targetDeliveryId || !targetField) return;
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(`delivery-${targetDeliveryId}-field-${targetField}`, {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetDeliveryId, targetField, isFlagged]);

  // Update expanded state when sections change
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    padding: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
  };

  const addButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#4CAF50',
    color: 'white',
    marginBottom: '16px',
  };

  const saveButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#4CAF50',
    color: 'white',
    marginRight: '8px',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#757575',
    color: 'white',
  };

  const editButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#2196F3',
    color: 'white',
    marginRight: '8px',
  };

  const deleteButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#f44336',
    color: 'white',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const thStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f8f9fa',
    fontWeight: '600',
    fontSize: '13px',
    textTransform: 'uppercase',
    color: '#555',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #eee',
    fontSize: '14px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    backgroundColor: 'white',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: '60px',
    resize: 'vertical',
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '40px',
    textAlign: 'center',
    color: '#888',
    backgroundColor: 'white',
    border: '1px solid #eee',
    borderRadius: '4px',
  };

  // Get batch records filtered by selected filling
  const getFilteredBatchRecords = (fillingId: string): BatchRecord[] => {
    if (!fillingId) return [];
    return batchRecords.filter((br) => br.productId === fillingId);
  };

  // Validate required fields
  const validateLog = (log: DeliveryLog): boolean => {
    if (
      !log.deliveryDate ||
      !log.deliveryTime ||
      !log.fillingId ||
      !log.batchRecordId ||
      !log.quantity ||
      !log.customerDestination ||
      log.transportationTemp === undefined ||
      log.transportationTemp === null ||
      !log.initials
    ) {
      alert('Please fill in all required fields');
      return false;
    }
    return true;
  };

  // Handlers for adding new log
  const handleStartAdd = () => {
    setNewLog(createBlankDeliveryLog());
    setIsAddingNew(true);
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewLog(createBlankDeliveryLog());
  };

  const handleSaveNew = () => {
    if (!validateLog(newLog)) return;

    // Find filling and batch record names to populate
    const filling = fillings.find((f) => f.id === newLog.fillingId);
    const batchRecord = batchRecords.find((br) => br.id === newLog.batchRecordId);

    const logToSave = {
      ...newLog,
      fillingName: filling?.name || '',
      batchLotNumber: batchRecord?.batchLotNumber || '',
    };

    onAdd(logToSave);
    handleCancelAdd();
  };

  const handleNewLogChange = (field: keyof DeliveryLog, value: any) => {
    setNewLog((prev) => {
      const updated = { ...prev, [field]: value };
      // If filling changes, reset batch record
      if (field === 'fillingId') {
        updated.batchRecordId = '';
        updated.batchLotNumber = '';
      }
      return updated;
    });
  };

  // Handlers for editing existing log
  const handleStartEdit = (log: DeliveryLog) => {
    setEditingId(log.id);
    setEditLog({ ...log });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditLog(null);
  };

  const handleSaveEdit = () => {
    if (!editLog || !validateLog(editLog)) return;

    // Find filling and batch record names to populate
    const filling = fillings.find((f) => f.id === editLog.fillingId);
    const batchRecord = batchRecords.find((br) => br.id === editLog.batchRecordId);

    const logToSave = {
      ...editLog,
      fillingName: filling?.name || '',
      batchLotNumber: batchRecord?.batchLotNumber || '',
    };

    onUpdate(logToSave);
    handleCancelEdit();
  };

  const handleEditLogChange = (field: keyof DeliveryLog, value: any) => {
    if (!editLog) return;
    setEditLog((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      // If filling changes, reset batch record
      if (field === 'fillingId') {
        updated.batchRecordId = '';
        updated.batchLotNumber = '';
      }
      return updated;
    });
  };

  // Handler for delete
  const handleDelete = (logId: string) => {
    if (window.confirm('Are you sure you want to delete this delivery log?')) {
      onDelete(logId);
    }
  };

  // Render form row (for adding or editing)
  const renderFormRow = (
    log: DeliveryLog,
    onChange: (field: keyof DeliveryLog, value: any) => void,
    onSave: () => void,
    onCancel: () => void
  ) => {
    const filteredBatches = getFilteredBatchRecords(log.fillingId);

    return (
      <tr style={{ backgroundColor: '#f9f9f9' }}>
        <td style={tdStyle}>
          <input
            type="date"
            value={log.deliveryDate}
            onChange={(e) => onChange('deliveryDate', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <input
            type="time"
            value={log.deliveryTime}
            onChange={(e) => onChange('deliveryTime', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <select
            value={log.fillingId}
            onChange={(e) => onChange('fillingId', e.target.value)}
            style={selectStyle}
          >
            <option value="">Select product...</option>
            {fillings.map((filling) => (
              <option key={filling.id} value={filling.id}>
                {filling.name}
              </option>
            ))}
          </select>
        </td>
        <td style={tdStyle}>
          <select
            value={log.batchRecordId}
            onChange={(e) => onChange('batchRecordId', e.target.value)}
            style={selectStyle}
            disabled={!log.fillingId}
          >
            <option value="">
              {!log.fillingId
                ? 'Select product first'
                : filteredBatches.length === 0
                ? 'No batches for this product'
                : 'Select batch...'}
            </option>
            {filteredBatches.map((batch) => (
              <option key={batch.id} value={batch.id}>
                {batch.batchLotNumber}
              </option>
            ))}
          </select>
        </td>
        <td style={tdStyle}>
          <input
            type="number"
            step="0.01"
            value={log.quantity}
            onChange={(e) => onChange('quantity', parseFloat(e.target.value) || 0)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <input
            type="text"
            value={log.customerDestination}
            onChange={(e) => onChange('customerDestination', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <input
            type="number"
            step="0.1"
            value={log.transportationTemp ?? ''}
            onChange={(e) => onChange('transportationTemp', parseFloat(e.target.value) || 0)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <input
            type="text"
            maxLength={50}
            value={log.initials ?? ''}
            onChange={(e) => onChange('initials', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <textarea
            value={log.correctiveAction || ''}
            onChange={(e) => onChange('correctiveAction', e.target.value)}
            style={textareaStyle}
            placeholder="Optional"
          />
        </td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onSave} style={saveButtonStyle}>
              Save
            </button>
            <button onClick={onCancel} style={cancelButtonStyle}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // Render display row
  const renderDisplayRow = (log: DeliveryLog) => {
    return (
      <tr key={log.id} style={{ transition: 'background-color 0.2s' }}>
        <td id={`delivery-${log.id}-field-deliveryDate`} style={tdStyle}>{log.deliveryDate}</td>
        <td id={`delivery-${log.id}-field-deliveryTime`} style={tdStyle}>{log.deliveryTime}</td>
        <td id={`delivery-${log.id}-field-fillingName`} style={tdStyle}>{log.fillingName || 'N/A'}</td>
        <td id={`delivery-${log.id}-field-batchLotNumber`} style={tdStyle}>
          {log.batchLotNumber ? (
            <ClickableIdentifier value={log.batchLotNumber} type="batchLotNumber" onClick={openTraceability} />
          ) : 'N/A'}
        </td>
        <td id={`delivery-${log.id}-field-quantity`} style={tdStyle}>{log.quantity.toFixed(2)}</td>
        <td id={`delivery-${log.id}-field-customerDestination`} style={tdStyle}>{log.customerDestination}</td>
        <td id={`delivery-${log.id}-field-transportationTemp`} style={tdStyle}>{log.transportationTemp != null ? log.transportationTemp.toFixed(1) : '-'}</td>
        <td id={`delivery-${log.id}-field-initials`} style={tdStyle}>{log.initials || '-'}</td>
        <td id={`delivery-${log.id}-field-correctiveAction`} style={tdStyle}>{log.correctiveAction || '-'}</td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => handleStartEdit(log)} style={editButtonStyle}>
              Edit
            </button>
            <button onClick={() => handleDelete(log.id)} style={deleteButtonStyle}>
              Delete
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // Table header component
  const renderTableHeader = () => (
    <thead>
      <tr>
        <th style={thStyle}>Delivery Date</th>
        <th style={thStyle}>Time</th>
        <th style={thStyle}>Product</th>
        <th style={thStyle}>Batch #</th>
        <th style={thStyle}>Quantity</th>
        <th style={thStyle}>Customer</th>
        <th style={thStyle}>Temp °C</th>
        <th style={thStyle}>Initials</th>
        <th style={thStyle}>Corrective Action</th>
        <th style={thStyle}>Actions</th>
      </tr>
    </thead>
  );

  // Render a log row (handles both edit and display mode)
  const renderLogRow = (log: DeliveryLog) => {
    if (editingId === log.id && editLog) {
      return renderFormRow(
        editLog,
        handleEditLogChange,
        handleSaveEdit,
        handleCancelEdit
      );
    }
    return renderDisplayRow(log);
  };

  return (
    <div style={containerStyle} className="table-container-mobile">
      {/* Add button */}
      <button
        onClick={handleStartAdd}
        style={addButtonStyle}
        disabled={isAddingNew || editingId !== null}
      >
        + Add Delivery
      </button>

      {/* Add new form (shown as standalone table when adding) */}
      {isAddingNew && (
        <div className="table-wrapper-mobile">
          <table style={tableStyle}>
            {renderTableHeader()}
            <tbody>
              {renderFormRow(newLog, handleNewLogChange, handleSaveNew, handleCancelAdd)}
            </tbody>
          </table>
        </div>
      )}

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search delivery logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* Empty state */}
      {logs.length === 0 && !isAddingNew && (
        <div style={emptyStateStyle}>
          No delivery logs yet. Click &quot;Add Delivery&quot; to create one.
        </div>
      )}

      {/* No search results */}
      {logs.length > 0 && filteredLogs.length === 0 && !isAddingNew && (
        <div style={emptyStateStyle}>
          No delivery logs match your search for "{searchTerm}".
        </div>
      )}

      {/* Sections with logs */}
      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div className="table-wrapper-mobile">
              <table style={tableStyle}>
                {renderTableHeader()}
                <tbody>
                  {section.items.map((log) => renderLogRow(log))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
